/*
 * entity-api.js — the entity engine's binding to the app-wide `request()`
 * API-service (SUBSYSTEMS Pattern C) plus the `registerModuleSafe` Vuex helper.
 *
 * Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
 * (request(endpoint, params, resultKey) against Navixy API v2; the session_key
 * cookie value attached to every call as the body field `hash`; resultKey plucks
 * a sub-path of the JSON envelope), internal impl re-authored.
 *
 * This is a self-contained mirror of the canonical helper that Engine C
 * (`src/api/request.js`) owns app-wide — kept local so the entity engine builds
 * and runs standalone. When the canonical service is present, re-point the two
 * re-exports below at it; the signatures are identical.
 */

import axios from 'axios';

/* CONFIG.apiUrl — Navixy/SquareGPS JSON API root. Overridable via Vite env. */
const API_URL =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL) ||
  'https://api.lacak.io/api/';

/* Cookie key names (Config.cookies.key: _default "session_key", external "hash"). */
const SESSION_COOKIE = 'session_key';

/** Read the session_key cookie (the value stamped onto every call as `hash`). */
export function readSessionKey() {
  if (typeof document === 'undefined' || !document.cookie) return '';
  const hit = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.indexOf(SESSION_COOKIE + '=') === 0);
  return hit ? decodeURIComponent(hit.slice(SESSION_COOKIE.length + 1)) : '';
}

/** Pluck a lodash-style path ("value", "list", "list[0]") from the envelope. */
function pluck(data, resultKey) {
  if (!resultKey) return data;
  const parts = String(resultKey).replace(/\[(\w+)\]/g, '.$1').split('.').filter(Boolean);
  let out = data;
  for (const p of parts) {
    if (out == null) return undefined;
    out = out[p];
  }
  return out;
}

/**
 * request(endpoint, params, resultKey[, skipErrors[, skipHash]])
 *   - endpoint : verbatim Navixy action string ("employee/list", "entity/read", …)
 *   - params   : POST body; the session_key is injected as `hash` unless skipHash
 *   - resultKey: optional sub-path to return from the response
 * Returns a Promise of the (optionally plucked) response body.
 */
export function request(endpoint, params = {}, resultKey, skipErrors = false, skipHash = false) {
  const body = Object.assign({}, params);
  if (!skipHash && body.hash == null) body.hash = readSessionKey();
  return axios
    .post(API_URL + endpoint, body, { headers: { 'Content-Type': 'application/json' } })
    .then((r) => (resultKey ? pluck(r.data, resultKey) : r.data))
    .catch((err) => {
      if (!skipErrors && typeof console !== 'undefined') {
        // canonical service maps status.code {2,3,4,11,103,no_hash} -> forced logout
        console.error('[entity-api] request failed:', endpoint, err && err.message);
      }
      throw err;
    });
}

/**
 * requestUpload(endpoint, formFields, file) — multipart sibling used by
 * avatar/file uploads; stamps `hash` as a form field.
 */
export function requestUpload(endpoint, formFields = {}, file) {
  const fd = new FormData();
  fd.append('hash', readSessionKey());
  Object.keys(formFields).forEach((k) => fd.append(k, formFields[k]));
  if (file) fd.append('file', file);
  return axios.post(API_URL + endpoint, fd).then((r) => r.data);
}

/**
 * registerModuleSafe(store, path, module) — idempotent namespaced-module
 * registration (Pattern C). No-ops if the module is already attached, so
 * repeated screen mounts (or HMR) don't throw "module already registered".
 * `path` may be a string or an array of segments.
 */
export function registerModuleSafe(store, path, module, options = { preserveState: false }) {
  if (!store || typeof store.hasModule !== 'function') {
    // very old Vuex without hasModule: guard on internal registry
    const key = Array.isArray(path) ? path.join('/') : path;
    if (store && store._modules && store._modules.root && store._modules.root._children &&
        store._modules.root._children[key]) {
      return false;
    }
  } else if (store.hasModule(path)) {
    return false;
  }
  store.registerModule(path, module, options);
  return true;
}

/** unregisterModuleSafe(store, path) — teardown counterpart. */
export function unregisterModuleSafe(store, path) {
  if (!store) return false;
  if (typeof store.hasModule === 'function' && !store.hasModule(path)) return false;
  store.unregisterModule(path);
  return true;
}

export default { request, requestUpload, readSessionKey, registerModuleSafe, unregisterModuleSafe };
